import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { EmailTemplate } from '@vedjegy/shared';
import { EMAIL_TEMPLATE_VARIABLES } from '@vedjegy/shared';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const RECIPIENT_COLORS: Record<string, { bg: string; text: string }> = {
  'Pályázó':   { bg: '#eff6ff', text: '#1d4ed8' },
  'Bíráló':    { bg: '#f5f3ff', text: '#7c3aed' },
  'Meghívott': { bg: '#f1f5f9', text: '#475569' },
};

const recipientBadgeStyle = (recipient: string): React.CSSProperties => {
  const colors = RECIPIENT_COLORS[recipient] ?? { bg: '#f1f5f9', text: '#374151' };
  return {
    fontSize: 11, fontWeight: 600, borderRadius: 4,
    padding: '1px 6px', lineHeight: '18px',
    background: colors.bg, color: colors.text,
  };
};

const SAMPLE_VARS: Record<string, string> = {
  companyName:  'Példa Kft.',
  appLink:      'https://vedjegy.hu/application/dashboard',
  totalScore:   '74',
  maxScore:     '150',
  percentage:   '49',
  threshold:    '60',
  description:  'Kérjük csatolja a hiányzó minőségirányítási tanúsítványt.',
  deadline:     '2026-08-15',
  inviteLink:   'https://vedjegy.hu/invite/example-token',
  expiresInDays: '7',
};

function renderPreview(html: string): string {
  return html.replace(/\{\{(\w+)\}\}/g, (_: string, key: string) => SAMPLE_VARS[key] ?? `{{${key}}}`);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminEmailTemplatesPage() {
  const [templates, setTemplates]         = useState<EmailTemplate[]>([]);
  const [loading, setLoading]             = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [subject, setSubject]             = useState('');
  const [htmlBody, setHtmlBody]           = useState('');
  const [previewMode, setPreviewMode]     = useState<'code' | 'preview'>('code');
  const [saving, setSaving]               = useState(false);
  const [savedMsg, setSavedMsg]           = useState('');
  const [error, setError]                 = useState('');

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.admin.getEmailTemplates();
      setTemplates(data.templates);
    } catch {
      setError('Nem sikerült betölteni a sablonokat.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadTemplates(); }, [loadTemplates]);

  // When user clicks an event in the sidebar
  function selectTemplate(tpl: EmailTemplate) {
    setSelectedEvent(tpl.event);
    setSubject(tpl.subject);
    setHtmlBody(tpl.htmlBody);
    setSavedMsg('');
    setError('');
    setPreviewMode('code');
  }

  async function handleSave() {
    if (!selectedEvent) return;
    setSaving(true);
    setError('');
    try {
      const res = await api.admin.updateEmailTemplate(selectedEvent, { subject, htmlBody });
      if (res.warnings?.length) {
        setSavedMsg(`Sablon mentve — figyelmeztetés: ${res.warnings.join('; ')}`);
      } else {
        setSavedMsg('Sablon mentve.');
      }
      await loadTemplates();
    } catch {
      setError('Mentés sikertelen.');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!selectedEvent) return;
    if (!confirm('Visszaállítja az alapértelmezett sablont? Az egyéni szöveg törlődik.')) return;
    setSaving(true);
    setError('');
    try {
      await api.admin.resetEmailTemplate(selectedEvent);
      setSavedMsg('Sablon visszaállítva.');
      // Refresh and re-select with default values
      const data = await api.admin.getEmailTemplates();
      setTemplates(data.templates);
      const refreshed = data.templates.find((t: EmailTemplate) => t.event === selectedEvent);
      if (refreshed) { setSubject(refreshed.subject); setHtmlBody(refreshed.htmlBody); }
    } catch {
      setError('Visszaállítás sikertelen.');
    } finally {
      setSaving(false);
    }
  }

  const current = templates.find((t) => t.event === selectedEvent) ?? null;
  const vars     = selectedEvent ? (EMAIL_TEMPLATE_VARIABLES[selectedEvent] ?? []) : [];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <AdminLayout>
    <div style={{ display: 'flex', height: '100%', minHeight: 0, gap: 0 }}>

      {/* Sidebar — event list */}
      <aside style={{
        width: 400, flexShrink: 0, borderRight: '1px solid #e5e7eb',
        overflowY: 'auto', background: '#f9fafb',
      }}>
        <div style={{ padding: '20px 16px 12px', fontWeight: 700, fontSize: 14, color: '#374151' }}>
          E-mail sablonok
        </div>
        {loading && (
          <div style={{ padding: '8px 16px', color: '#9ca3af', fontSize: 13 }}>Betöltés…</div>
        )}
        {templates.map((tpl) => (
          <button
            key={tpl.event}
            onClick={() => selectTemplate(tpl)}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '10px 16px', border: 'none', cursor: 'pointer',
              background: selectedEvent === tpl.event ? '#e0f2fe' : 'transparent',
              borderLeft: selectedEvent === tpl.event ? '3px solid #0ea5e9' : '3px solid transparent',
              fontWeight: selectedEvent === tpl.event ? 600 : 400,
              fontSize: 13, color: '#374151',
            }}
          >
            <span style={{
              display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
              marginRight: 8,
              background: tpl.isCustom ? '#22c55e' : '#d1d5db',
            }} />
            {tpl.label}
          </button>
        ))}
      </aside>

      {/* Editor pane */}
      <div style={{ flex: 1, padding: 28, overflowY: 'auto', minWidth: 0 }}>
        {!selectedEvent ? (
          <div style={{ color: '#9ca3af', marginTop: 60, textAlign: 'center' }}>
            Válasszon egy eseményt a bal oldali listából.
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
                {current?.label ?? selectedEvent}
              </h2>
              {current?.recipient && (
                <span style={recipientBadgeStyle(current.recipient)}>
                  {current.recipient}
                </span>
              )}
              {current?.isCustom && (
                <span style={{
                  fontSize: 12, background: '#dcfce7', color: '#15803d',
                  borderRadius: 4, padding: '2px 8px', fontWeight: 600,
                }}>
                  Egyéni
                </span>
              )}
            </div>

            {/* Subject field */}
            <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#374151' }}>
              Tárgy
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                borderRadius: 6, fontSize: 14, marginBottom: 20, boxSizing: 'border-box',
              }}
            />

            {/* Variable hints */}
            {vars.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                  Elérhető változók:{' '}
                </span>
                {vars.map((v: string) => (
                  <code
                    key={v}
                    onClick={() => setHtmlBody((prev) => prev + `{{${v}}}`)}
                    style={{
                      display: 'inline-block', marginRight: 6, marginBottom: 4,
                      padding: '2px 7px', background: '#f3f4f6', borderRadius: 4,
                      fontSize: 12, cursor: 'pointer', border: '1px solid #e5e7eb',
                      color: '#4b5563',
                    }}
                    title="Kattintson a beillesztéshez"
                  >
                    {`{{${v}}}`}
                  </code>
                ))}
              </div>
            )}

            {/* Code / Preview tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 0, borderBottom: '1px solid #e5e7eb' }}>
              {(['code', 'preview'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setPreviewMode(tab)}
                  style={{
                    padding: '8px 20px', fontSize: 13, fontWeight: 600,
                    border: 'none', borderBottom: previewMode === tab ? '2px solid #0ea5e9' : '2px solid transparent',
                    background: 'transparent', cursor: 'pointer',
                    color: previewMode === tab ? '#0ea5e9' : '#6b7280',
                  }}
                >
                  {tab === 'code' ? 'HTML szerkesztő' : 'Előnézet'}
                </button>
              ))}
            </div>

            {previewMode === 'code' ? (
              <textarea spellCheck={true} lang="hu"
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                rows={20}
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                  borderRadius: '0 0 6px 6px', fontSize: 13,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  resize: 'vertical', boxSizing: 'border-box',
                  borderTop: 'none',
                }}
              />
            ) : (
              <div style={{
                border: '1px solid #d1d5db', borderTop: 'none',
                borderRadius: '0 0 6px 6px', overflow: 'hidden',
              }}>
                <iframe
                  srcDoc={`<!DOCTYPE html><html><body style="font-family:sans-serif;padding:24px;color:#222;">${renderPreview(htmlBody)}</body></html>`}
                  style={{ width: '100%', height: 440, border: 'none' }}
                  sandbox="allow-same-origin"
                  title="E-mail előnézet"
                />
              </div>
            )}

            {/* Feedback messages */}
            {savedMsg && (
              <div style={{ marginTop: 12, color: '#16a34a', fontWeight: 600, fontSize: 13 }}>
                ✓ {savedMsg}
              </div>
            )}
            {error && (
              <div style={{ marginTop: 12, color: '#dc2626', fontWeight: 600, fontSize: 13 }}>
                {error}
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button
                onClick={() => void handleSave()}
                disabled={saving}
                style={{
                  padding: '10px 28px', background: '#0ea5e9', color: '#fff',
                  border: 'none', borderRadius: 6, fontWeight: 700,
                  fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Mentés…' : 'Mentés'}
              </button>
              {current?.isCustom && (
                <button
                  onClick={() => void handleReset()}
                  disabled={saving}
                  style={{
                    padding: '10px 20px', background: '#f3f4f6', color: '#374151',
                    border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600,
                    fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                >
                  Visszaállítás alapértelmezettre
                </button>
              )}
            </div>

            {/* Last updated */}
            {current?.isCustom && current.updatedAt && (
              <p style={{ marginTop: 16, fontSize: 12, color: '#9ca3af' }}>
                Utoljára módosítva: {new Date(current.updatedAt).toLocaleString('hu-HU')}
                {current.updatedByEmail ? ` — ${current.updatedByEmail}` : ''}
              </p>
            )}
          </>
        )}
      </div>
    </div>
    </AdminLayout>
  );
}
